// src/features/disciplinaryProblems/disciplinaryProblemsSlice.js

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { setError } from './errorSlice';

const initialState = {
  items: [],
  status: 'idle',
  error: null,
};

// Fetch all disciplinary problems
export const fetchDisciplinaryProblems = createAsyncThunk(
  'disciplinaryProblems/fetchAll',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const storedUser = localStorage.getItem('userInfo');
      if (!storedUser) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const { token } = JSON.parse(storedUser);
      if (!token) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const response = await axios.get(`${API_BASE_URL}/api/DisciplinaryProblems/fetch`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      return response.data;
    } catch (err) {
      dispatch(setError(err.message));
      return rejectWithValue(err.response?.data || 'Failed to fetch disciplinary problems');
    }
  }
);

// Create a new problem
export const createDisciplinaryProblem = createAsyncThunk(
  'disciplinaryProblems/create',
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const storedUser = localStorage.getItem('userInfo');
      if (!storedUser) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const { token } = JSON.parse(storedUser);
      if (!token) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const response = await axios.post(`${API_BASE_URL}/api/DisciplinaryProblems/create`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      return response.data;
    } catch (err) {
      dispatch(setError(err.message));
      return rejectWithValue(err.response?.data || 'Failed to create disciplinary problem');
    }
  }
);

// Edit an existing problem
export const editDisciplinaryProblem = createAsyncThunk(
  'disciplinaryProblems/edit',
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const storedUser = localStorage.getItem('userInfo');
      if (!storedUser) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const { token } = JSON.parse(storedUser);
      if (!token) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const response = await axios.put(`${API_BASE_URL}/api/DisciplinaryProblems/edit`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      return response.data;
    } catch (err) {
      dispatch(setError(err.message));
      return rejectWithValue(err.response?.data || 'Failed to edit disciplinary problem');
    }
  }
);

// Hard-delete a problem
export const deleteDisciplinaryProblem = createAsyncThunk(
  'disciplinaryProblems/delete',
  async (problemId, { rejectWithValue, dispatch }) => {
    try {
      const storedUser = localStorage.getItem('userInfo');
      if (!storedUser) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const { token } = JSON.parse(storedUser);
      if (!token) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      await axios.delete(`${API_BASE_URL}/api/DisciplinaryProblems/hardDelete/${problemId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return problemId;
    } catch (err) {
      dispatch(setError(err.message));
      return rejectWithValue(err.response?.data || 'Failed to delete disciplinary problem');
    }
  }
);

const disciplinaryProblemsSlice = createSlice({
  name: 'disciplinaryProblems',
  initialState,
  reducers: {
    resetProblemForm(state) {
      state.status = 'idle';
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDisciplinaryProblems.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchDisciplinaryProblems.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload;
      })
      .addCase(fetchDisciplinaryProblems.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(createDisciplinaryProblem.fulfilled, (state, action) => {
        state.items.unshift(action.payload);
      })
      .addCase(editDisciplinaryProblem.fulfilled, (state, action) => {
        const idx = state.items.findIndex(p => p.problemID === action.payload.problemID);
        if (idx !== -1) state.items[idx] = action.payload;
      })
      .addCase(deleteDisciplinaryProblem.fulfilled, (state, action) => {
        state.items = state.items.filter(p => p.problemID !== action.payload);
      });
  },
});

export const { resetProblemForm } = disciplinaryProblemsSlice.actions;
export default disciplinaryProblemsSlice.reducer;
